import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { coerce, satisfies } from 'semver';
import { io } from 'socket.io-client';

import {
  LicenseEventsChangedPayload,
  LicenseEventsJoinMetadata,
  LicenseEventsJoinPayload,
  LicenseEventsJoinResponse,
  LicenseInfo,
  LicenseScope,
} from '@common-lib';

import { ActiveLicenses } from './active-licenses.interface';
import { LicensesContext, LicensesContextValue } from './licenses-context';

declare global {
  var WS_CONFIG: {
    host?: string;
  };
}

const LOCALSTORAGE_KEY = 'license_consumer_cache';

const socket = io(WS_CONFIG?.host ? WS_CONFIG.host : undefined, {
  autoConnect: false,
  path: '/license-events',
  protocols: ['websocket'],
});

export interface LicenseConsumerProps {
  organizationId: string | undefined;
  scope: LicenseScope;
  metadata: LicenseEventsJoinMetadata;
  defaults: ActiveLicenses;
}

export const LicenseConsumer = ({
  children,
  defaults,
  organizationId,
  metadata,
  scope,
}: PropsWithChildren<LicenseConsumerProps>) => {
  const [state, setState] = useState<LicensesContextValue>(() => {
    const cachedLicenses = localStorage.getItem(LOCALSTORAGE_KEY);

    if (cachedLicenses) {
      /*
       * If we have previously fetched licenses we use those as the initial state and
       * when we later successfully connect to the license-service we will update the
       * state with the latest licenses.
       *
       * During normal usage this means that the user will be able to use the features
       * that require licenses immediately even if the license-service is not available
       * which speeds up the initial loading time of the application.
       *
       * Note: this means that the user could change the cached licenses and block the
       * license-service connections successfully hacking which features they should
       * be able to have access to.
       */
      const activeOnline = JSON.parse(cachedLicenses) satisfies ActiveLicenses;
      return {
        activeOnline: { ...defaults, ...activeOnline },
        defaults,
        isOnline: socket.active,
      };
    }

    return { activeOnline: undefined, defaults, isOnline: false };
  });

  useUpdateEffect(() => {
    if (state.activeOnline) {
      localStorage.setItem(
        LOCALSTORAGE_KEY,
        JSON.stringify(state.activeOnline),
      );
    }
  }, [state.activeOnline]);

  const handleLicenseChanges = useCallback(
    (value: LicenseEventsChangedPayload | LicenseEventsJoinResponse) => {
      setState((prev): LicensesContextValue => {
        const active = value.licenses.reduce(
          (acc: ActiveLicenses, license: LicenseInfo) => {
            if (typeof license.allowed === 'boolean') {
              acc[license.id] = license.allowed;
              return acc;
            } else {
              // if the allowed is a string, we assume it's a semver range
              // and we need to check if the current version satisfies it

              if (
                metadata.version === 'local' ||
                metadata.version.includes('dev') ||
                metadata.version.includes('latest')
              ) {
                // if the metadata version is 'local', we automatically allow the license.
                acc[license.id] = true;
                return acc;
              }
              const coercedVersion = coerce(metadata.version)?.version;
              acc[license.id] = coercedVersion
                ? satisfies(coercedVersion, license.allowed)
                : false;
              return acc;
            }
          },
          {} satisfies ActiveLicenses,
        );

        return {
          ...prev,
          activeOnline: active,
        };
      });
    },
    [],
  );

  const handleConnect = useCallback(() => {
    console.log('Connected to license service');
    socket
      .emitWithAck('join', {
        organizationId: organizationId!,
        scope,
        metadata,
      } satisfies LicenseEventsJoinPayload)
      .then(handleLicenseChanges)
      .then(() => setState((prev) => ({ ...prev, isOnline: true })));
  }, [organizationId]);

  const handleConnectError = useCallback((error: Error) => {
    if (socket.active) {
      // temporary failure, the socket will automatically try to reconnect
    } else {
      // the connection was denied by the server
      // in that case, `socket.connect()` must be manually called in order to
      // reconnect but so far license service is not denying any connections
      // so this case should normally not happen.
      console.error('Failed to connect to license service:', error);
    }
  }, []);

  const handleDisconnect = useCallback(() => {
    console.log('Disconnected from license service');
  }, []);

  useEffect(() => {
    socket.on('onChanged', handleLicenseChanges);
    socket.on('connect', handleConnect);
    socket.on('connect_error', handleConnectError);
    socket.on('disconnect', handleDisconnect);

    return () => {
      socket.off('onChanged', handleLicenseChanges);
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleConnectError);
      socket.off('disconnect', handleDisconnect);
    };
  });

  useEffect(() => {
    if (organizationId) {
      socket.connect();
    }
  }, [organizationId]);

  return (
    <LicensesContext.Provider value={state}>
      {children}
    </LicensesContext.Provider>
  );
};
